import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";

// --- Types ---

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  disabled: boolean;
  createdAt: string;
};

export type UserPermission = {
  groupId: string;
  includeDescendants: boolean;
  permissions: string[];
};

export type Role = {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  permissions: string[];
  tenantId: string | null;
  isSystem: boolean;
};

export type RoleAssignment = {
  id: string;
  userId: string;
  roleId: string;
  groupId: string;
  includeDescendants: boolean;
  roleName?: string;
  roleDisplayName?: string;
  groupName?: string;
};

type UsersContextType = {
  // Data
  users: User[];
  roles: Role[];
  loading: boolean;
  error: string | null;

  // Selection
  selectedUserId: string | null;
  selectUser: (id: string | null) => void;

  // CRUD operations
  fetchUsers: () => Promise<void>;
  fetchRoles: () => Promise<void>;
  getUserById: (id: string) => Promise<User>;
  createUser: (data: CreateUserData) => Promise<User>;
  updateUser: (id: string, data: UpdateUserData) => Promise<User>;
  deleteUser: (id: string) => Promise<void>;

  // Role assignments
  getUserRoleAssignments: (userId: string) => Promise<RoleAssignment[]>;
  assignRole: (userId: string, roleId: string, groupId: string, includeDescendants: boolean) => Promise<void>;
  removeRoleAssignment: (userId: string, roleId: string, groupId: string) => Promise<void>;

  // User status
  setUserDisabled: (userId: string, disabled: boolean) => Promise<User>;
};

type CreateUserData = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  permissions?: UserPermission[];
  roleAssignments?: { roleId: string; groupId: string; includeDescendants: boolean }[];
};

type UpdateUserData = {
  email?: string;
  firstName?: string;
  lastName?: string;
  // Optional admin-initiated password reset. Omit/leave empty to keep the current password.
  password?: string;
};

const UsersContext = createContext<UsersContextType | null>(null);

// --- Provider ---

type Props = {
  children: React.ReactNode;
};

export const UsersProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // --- Fetch users ---

  const fetchUsers = useCallback(async () => {
    if (!currentTenantId) {
      setUsers([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: currentTenantId });
      const data = await apiClient.get<{ users: User[] }>(`/api/users?${params}`);
      setUsers(data.users);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, currentTenantId]);

  // --- Fetch roles ---

  const fetchRoles = useCallback(async () => {
    try {
      const data = await apiClient.get<{ roles: Role[] }>("/api/roles");
      setRoles(data.roles);
    } catch (err: any) {
      console.error("Failed to fetch roles:", err);
    }
  }, [apiClient]);

  // --- Fetch a single user on demand ---
  // Used when editing a user that may not be in the tenant-scoped `users` list
  // (e.g. a platform admin editing a user in another tenant).

  const getUserById = useCallback(
    async (id: string): Promise<User> => {
      const data = await apiClient.get<{ user: User }>(`/api/users/${id}`);
      return data.user;
    },
    [apiClient],
  );

  // --- Create user ---

  const createUser = useCallback(
    async (data: CreateUserData): Promise<User> => {
      const result = await apiClient.post<{ user: User }>("/api/users", data);
      setUsers((prev) => [...prev, result.user]);
      return result.user;
    },
    [apiClient],
  );

  // --- Update user ---

  const updateUser = useCallback(
    async (id: string, data: UpdateUserData): Promise<User> => {
      const result = await apiClient.put<{ user: User }>(`/api/users/${id}`, data);
      setUsers((prev) => prev.map((u) => (u.id === id ? result.user : u)));
      return result.user;
    },
    [apiClient],
  );

  // --- Delete user ---

  const deleteUser = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`/api/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setSelectedUserId((current) => (current === id ? null : current));
    },
    [apiClient],
  );

  // --- Selection ---

  const selectUser = useCallback((id: string | null) => {
    setSelectedUserId(id);
  }, []);

  // --- Role Assignments ---

  const getUserRoleAssignments = useCallback(
    async (userId: string): Promise<RoleAssignment[]> => {
      const data = await apiClient.get<{ roleAssignments: RoleAssignment[] }>(`/api/users/${userId}/roles`);
      return data.roleAssignments;
    },
    [apiClient],
  );

  const assignRole = useCallback(
    async (userId: string, roleId: string, groupId: string, includeDescendants: boolean): Promise<void> => {
      await apiClient.post(`/api/users/${userId}/roles`, { roleId, groupId, includeDescendants });
    },
    [apiClient],
  );

  const removeRoleAssignment = useCallback(
    async (userId: string, roleId: string, groupId: string): Promise<void> => {
      await apiClient.delete(`/api/users/${userId}/roles/${roleId}/${groupId}`);
    },
    [apiClient],
  );

  // --- Set user disabled status ---

  const setUserDisabled = useCallback(
    async (userId: string, disabled: boolean): Promise<User> => {
      const result = await apiClient.patch<{ user: User }>(`/api/users/${userId}`, { disabled });
      setUsers((prev) => prev.map((u) => (u.id === userId ? result.user : u)));
      return result.user;
    },
    [apiClient],
  );

  // --- Load on mount ---

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  // --- Context value ---

  const value: UsersContextType = {
    users,
    roles,
    loading,
    error,
    selectedUserId,
    selectUser,
    fetchUsers,
    fetchRoles,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    getUserRoleAssignments,
    assignRole,
    removeRoleAssignment,
    setUserDisabled,
  };

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
};

// --- Hook ---

export const useUsers = (): UsersContextType => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsers must be used within a UsersProvider");
  }
  return context;
};
